import Papa from 'papaparse';
import {
  CDMO_IMPORT_COLUMNS,
  CDMO_IMPORT_MAX_FILE_BYTES,
  CDMO_IMPORT_MAX_ROWS,
  ERROR_REPORT_COLUMNS,
  columnLabel,
} from './columns';

/** One data row as uploaded: every template header mapped to its cell, exactly as written. */
export interface CdmoImportRawRow {
  /** 1-based position among the data rows (the header row is not counted). */
  rowNumber: number;
  values: Record<string, string>;
  /** How many cells the row actually had; a mismatch with the header is reported on the row. */
  cellCount: number;
}

/**
 * Why a whole file was rejected: `template` — the header row is not the template's; `format` — the
 * text is not readable CSV; `empty` — no CDMO rows; `limit` — too many rows.
 */
export type CdmoImportRejectionKind = 'template' | 'format' | 'empty' | 'limit';

export type CdmoImportParseResult =
  | { ok: true; headers: string[]; rows: CdmoImportRawRow[] }
  | { ok: false; kind: CdmoImportRejectionKind; error: string; details: string[] };

export interface HeaderCheck {
  missing: string[];
  unexpected: string[];
  duplicated: string[];
  /** Position-by-position mismatches, reported only when the right columns are present. */
  outOfOrder: string[];
}

const BYTE_ORDER_MARK = /^\uFEFF/;

/** What the browser puts in place of bytes that are not valid UTF-8 when it reads the file. */
const REPLACEMENT_CHARACTER = '\uFFFD';

/**
 * The one save option to recommend. Excel's plain "CSV (Comma delimited)" writes the Windows code
 * page, not UTF-8, which is what damages the characters `findUnreadableText` looks for.
 */
const SAVE_AS_CSV_UTF8 = 'Save it again as "CSV UTF-8 (Comma delimited) (*.csv)" (in Excel: File \u2192 Save As)';

const formatBytes = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

/**
 * Rejects a file before it is read: wrong extension or over the size limit.
 *
 * @returns The reason, or null when the file may be parsed
 */
export function checkFileBeforeParsing(file: { name: string; size: number }): string | null {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    return `"${file.name}" is not a CSV file. Upload the .csv file exported from the import template.`;
  }
  if (file.size === 0) {
    return `"${file.name}" is empty.`;
  }
  if (file.size > CDMO_IMPORT_MAX_FILE_BYTES) {
    return `"${file.name}" is ${formatBytes(file.size)}. The limit is ${formatBytes(CDMO_IMPORT_MAX_FILE_BYTES)} per file — split it into smaller files.`;
  }
  return null;
}

/**
 * Compares an uploaded header row with the template's, which must match exactly and in order.
 *
 * @returns null when the headers match, otherwise what is wrong with them
 */
export function checkHeaders(headers: readonly string[]): HeaderCheck | null {
  const expected = new Set(CDMO_IMPORT_COLUMNS);
  const seen = new Set<string>();
  const duplicated = new Set<string>();
  for (const header of headers) {
    if (seen.has(header)) duplicated.add(header);
    seen.add(header);
  }

  const missing = CDMO_IMPORT_COLUMNS.filter((column) => !seen.has(column));
  const unexpected = [...seen].filter((header) => !expected.has(header));

  const outOfOrder: string[] = [];
  if (missing.length === 0 && unexpected.length === 0 && duplicated.size === 0) {
    CDMO_IMPORT_COLUMNS.forEach((column, index) => {
      if (headers[index] !== column) {
        outOfOrder.push(`column ${index + 1} should be "${column}" but is "${headers[index]}"`);
      }
    });
  }

  const check = { missing, unexpected, duplicated: [...duplicated], outOfOrder };
  const hasProblem = missing.length + unexpected.length + duplicated.size + outOfOrder.length > 0;
  return hasProblem ? check : null;
}

/** Human-readable lines describing a header mismatch, for the rejection message. */
export function describeHeaderCheck(check: HeaderCheck): string[] {
  const lines: string[] = [];
  const MAX_LISTED = 8;
  const list = (values: string[]) => {
    const shown = values.slice(0, MAX_LISTED).map((v) => `"${v}"`).join(', ');
    return values.length > MAX_LISTED ? `${shown} and ${values.length - MAX_LISTED} more` : shown;
  };
  if (check.missing.length) lines.push(`Missing columns: ${list(check.missing)}`);
  if (check.unexpected.length) lines.push(`Unexpected columns: ${list(check.unexpected)}`);
  if (check.duplicated.length) lines.push(`Columns that appear more than once: ${list(check.duplicated)}`);
  if (check.outOfOrder.length) {
    const shown = check.outOfOrder.slice(0, 5);
    const more = check.outOfOrder.length - shown.length;
    lines.push(`Columns out of order: ${shown.join('; ')}${more > 0 ? `; and ${more} more` : ''}`);
  }
  return lines;
}

/**
 * Cells holding text the browser could not read, as "Row n (spreadsheet row n+1), Column: "…"" —
 * the first few only. A file that is not UTF-8 turns dashes, curly quotes and accented letters into
 * U+FFFD, so every option with an em dash would fail to match and free text would be saved damaged.
 */
function findUnreadableText(headers: readonly string[], dataRows: readonly string[][]): string[] {
  const MAX_LISTED = 3;
  const found: string[] = [];
  dataRows.forEach((cells, index) => {
    cells.forEach((cell, columnIndex) => {
      const at = cell.indexOf(REPLACEMENT_CHARACTER);
      if (at === -1 || found.length >= MAX_LISTED) return;
      const excerpt = cell.slice(Math.max(0, at - 30), at + 30).trim();
      const column = columnLabel(headers[columnIndex] ?? `column ${columnIndex + 1}`);
      found.push(`Row ${index + 1} (spreadsheet row ${index + 2}), ${column}: "${excerpt}"`);
    });
  });
  return found;
}

const isBlank = (cell: string) => cell.trim() === '';

/**
 * How many empty cells end the header row. A spreadsheet with a used but empty column after the data
 * writes a trailing comma on every line, which would otherwise read as an unnamed column.
 */
function countTrailingBlankHeaders(headerRow: readonly string[]): number {
  let count = 0;
  while (count < headerRow.length && isBlank(headerRow[headerRow.length - 1 - count])) count += 1;
  return count;
}

/**
 * A data row without the cells under the header's trailing blank columns. They are dropped only when
 * they are all empty and the row is no longer than the header: a value there, or an unquoted comma on
 * a row that has its padding, stays, so the row's cell count still reports it. An unquoted comma on a
 * row missing its padding cannot be told from padding, like two comma mistakes that cancel out in any
 * file: the row is read shifted, and its shifted cells are reported by validation instead.
 */
function dropPaddingCells(cells: string[], headerLength: number, padding: number): string[] {
  const width = headerLength - padding;
  if (padding === 0 || cells.length > headerLength || !cells.slice(width).every(isBlank)) return cells;
  return cells.slice(0, width);
}

/** papaparse's quote error, in words — its row index counts the header as 0, so it is the data row. */
function describeQuoteError(error: Papa.ParseError): string {
  const where = typeof error.row === 'number' && error.row > 0 ? `Row ${error.row}` : 'Header row';
  const what =
    error.code === 'MissingQuotes'
      ? 'a quoted cell is opened but never closed'
      : 'a quoted cell has extra text after its closing quote';
  return `${where}: ${what}.`;
}

/**
 * The rejection for a header that is not the template's: what is wrong, and — for the common
 * mistakes (semicolon or tab separators, a missing header row) — what to do about it.
 */
function describeTemplateMismatch(headers: readonly string[], check: HeaderCheck): { error: string; details: string[] } {
  const expectedCount = CDMO_IMPORT_COLUMNS.length;

  if (headers.length === 1 && /[;\t]/.test(headers[0])) {
    const separator = headers[0].includes(';') ? 'semicolons' : 'tabs';
    return {
      error: `This file uses ${separator} between columns instead of commas, so its columns cannot be read.`,
      details: [`${SAVE_AS_CSV_UTF8}, or download the template and paste the data into it.`],
    };
  }

  const known = new Set(CDMO_IMPORT_COLUMNS);
  if (headers.length > 1 && headers.every((header) => !known.has(header))) {
    return {
      error: "The first row of this file is not the import template's header row.",
      details: [
        `The first row should start with "${CDMO_IMPORT_COLUMNS[0]}", "${CDMO_IMPORT_COLUMNS[1]}", "${CDMO_IMPORT_COLUMNS[2]}" … but starts with "${headers[0]}".`,
        'If the header row was deleted, download the template and paste your rows underneath its header.',
      ],
    };
  }

  return {
    error: 'This file does not follow the import template. The header row must match the template exactly — same column names, same order.',
    details: [
      ...(headers.length !== expectedCount
        ? [`The template has ${expectedCount} columns; this file has ${headers.length}.`]
        : []),
      ...describeHeaderCheck(check),
    ],
  };
}

/**
 * Parses an uploaded CDMO import CSV (RFC 4180: quoted commas, doubled quotes and line breaks
 * inside cells) and checks it against the template before any row is looked at.
 *
 * Nothing here judges cell values — that is `validateRow`. A file is rejected only when it cannot be
 * trusted as a whole: unparseable quoting, a header that differs from the template, no data rows,
 * more rows than the limit, or text that could not be read because the file is not UTF-8.
 */
export function parseCdmoImportCsv(text: string): CdmoImportParseResult {
  const parsed = Papa.parse<string[]>(text.replace(BYTE_ORDER_MARK, ''), {
    delimiter: ',',
    header: false,
    // Rows made only of empty cells are spreadsheet padding, not data.
    skipEmptyLines: 'greedy',
  });

  const quoteErrors = parsed.errors.filter((error) => error.type === 'Quotes');
  if (quoteErrors.length > 0) {
    return {
      ok: false,
      kind: 'format',
      error:
        'The file could not be read as CSV: a quoted cell is not closed properly. A cell that starts with a double quote (") must end with one, and any quote inside it must be doubled ("").',
      details: quoteErrors.slice(0, 5).map(describeQuoteError),
    };
  }

  const [headerRow, ...paddedDataRows] = parsed.data;
  if (!headerRow) {
    return { ok: false, kind: 'empty', error: 'The file is empty.', details: [] };
  }

  const padding = countTrailingBlankHeaders(headerRow);
  const dataRows = paddedDataRows.map((cells) => dropPaddingCells(cells, headerRow.length, padding));
  const allHeaders = headerRow.slice(0, headerRow.length - padding).map((header) => header.trim());
  // A re-uploaded error report still ends with its two report columns; they are not data.
  const reportColumnCount = ERROR_REPORT_COLUMNS.every(
    (column, index) => allHeaders[allHeaders.length - ERROR_REPORT_COLUMNS.length + index] === column
  )
    ? ERROR_REPORT_COLUMNS.length
    : 0;
  const headers = allHeaders.slice(0, allHeaders.length - reportColumnCount);
  const headerCheck = checkHeaders(headers);
  if (headerCheck) {
    return { ok: false, kind: 'template', ...describeTemplateMismatch(headers, headerCheck) };
  }

  if (dataRows.length === 0) {
    return {
      ok: false,
      kind: 'empty',
      error: 'The file has the header row but no CDMO rows. Add one row per CDMO below the header.',
      details: [],
    };
  }

  if (dataRows.length > CDMO_IMPORT_MAX_ROWS) {
    return {
      ok: false,
      kind: 'limit',
      error: `The file has ${dataRows.length} CDMO rows. The limit is ${CDMO_IMPORT_MAX_ROWS} per file — split it into smaller files.`,
      details: [],
    };
  }

  const unreadable = findUnreadableText(headers, dataRows);
  if (unreadable.length > 0) {
    return {
      ok: false,
      kind: 'format',
      error:
        'Some characters in this file could not be read — they show as "�". This happens when a file is not saved as UTF-8, for example with Excel\'s "CSV (Comma delimited)", and it damages dashes, curly quotes and accented letters.',
      details: [`${SAVE_AS_CSV_UTF8}, then upload that file.`, ...unreadable],
    };
  }

  const rows = dataRows.map((cells, index) => {
    const values: Record<string, string> = {};
    CDMO_IMPORT_COLUMNS.forEach((column, columnIndex) => {
      values[column] = cells[columnIndex] ?? '';
    });
    return { rowNumber: index + 1, values, cellCount: cells.length - reportColumnCount };
  });

  return { ok: true, headers, rows };
}
